import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Alert from 'react-bootstrap/Alert';
import Header from '../../components/Header';
import ResponsiveTableRegion from '../../components/ResponsiveTableRegion';
import OrganizationFilter from '../../components/OrganizationFilter';
import OrganizationPicker from '../../components/OrganizationPicker';
import { ventrisConfig } from '../../ventris.config';
import { focusFirstInvalidField } from '../../accessibility/focus';
import AsyncStatus from '../../components/AsyncStatus';

import GroupPageIcon from '../../icons/GroupPageIcon';
import FilterIcon from '../../icons/FilterIcon';
import GroupIcon from '../../icons/GroupIcon';
import { getUseCaseTerminology } from '../../useCase';

type AdminUsersProps = {
};

function AdminUsers({}: AdminUsersProps) {
  const terms = getUseCaseTerminology();
  const location = useLocation();
  const navigate = useNavigate();
  const hierarchyEditHandled = useRef(false);
  const [user, setUser] = useState([] as any);
  const [organizations, setOrganizations] = useState([] as any);
  const [users, setUsers] = useState([] as any);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [selectedUser, setSelectedUser] = useState({} as any);
  const [selectedUserId, setSelectedUserId] = useState("" as any);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [errorEdit, setErrorEdit] = useState("");
  const [validatedEdit, setValidatedEdit] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showActiveTeachersModal, setShowActiveTeachersModal] = useState(false);
  const [managedTeachers, setManagedTeachers] = useState([] as any[]);
  const [loadingManagedTeachers, setLoadingManagedTeachers] = useState(false);
  const [savingManagedTeachers, setSavingManagedTeachers] = useState(false);
  const [activeTeachersError, setActiveTeachersError] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [errorCreate, setErrorCreate] = useState("");
  const [validatedCreate, setValidatedCreate] = useState(false);
  const [errorUpload, setErrorUpload] = useState("");
  const [uploadSummary, setUploadSummary] = useState("");
  const [validatedUpload, setValidatedUpload] = useState(false);
  const [uploadOrganizationId, setUploadOrganizationId] = useState("-1");
  const [uploadFile, setUploadFile] = useState(null as File | null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("");
  const [organizationId, setOrganizationId] = useState("-1");
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(20);
  const [totalSize, setTotalSize] = useState(0);
  const [sortFieldName, setSortFieldName] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const editFormRef = useRef(null as any);
  const createFormRef = useRef(null as any);
  const uploadFormRef = useRef(null as any);

  const passwordPolicyMessage = "Password must be at least 8 characters and include 1 number, 1 symbol, and 1 capital letter.";

  const validatePasswordValue = (value: string) => {
    const hasMinLength = value.length >= 8;
    const hasNumber = /\d/.test(value);
    const hasSymbol = /[^A-Za-z0-9]/.test(value);
    const hasUppercase = /[A-Z]/.test(value);
    return hasMinLength && hasNumber && hasSymbol && hasUppercase;
  };

  const validatePasswordRequired = () => {
    if (!validatePasswordValue(password)) {
      return passwordPolicyMessage;
    }
    return "";
  };

  const validatePasswordOptional = () => {
    if (password.trim().length === 0) {
      return "";
    }
    return validatePasswordRequired();
  };

  useEffect(() => {
    let token = sessionStorage.getItem("token");
    let currentUser = sessionStorage.getItem("current_user");
    setUser(JSON.parse(currentUser!));

    fetch(ventrisConfig.url + '/api/organizations?compact=true', {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setOrganizations(data);
      setOrganizationsLoaded(true);
    });
  }, []);

  useEffect(() => {
    loadUsers(page);
  }, [page, sizePerPage, sortFieldName, sortDirection, organizationFilter, debouncedSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
      if (page !== 1) {
        setPage(1);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const loadUsers = (pageNumber: number) => {
    let token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/users?page=' + pageNumber + '&sizePerPage=' + sizePerPage + '&sortField=' + sortFieldName + '&sortDirection=' + sortDirection + '&organization=' + organizationFilter + '&search=' + encodeURIComponent(debouncedSearch), {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setUsers(data.users);
      setTotalSize(data.total_records);
    });
  }

  const editUser = (user: any) => {
    setSelectedUser(user);
    setSelectedUserId(user.id);
    setEmail(user.email);
    setPassword("");
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setRole(user.role);
    setOrganizationId(user.organization_id);
    setShowEditModal(true);
  }

  useEffect(() => {
    const hierarchyUser = (location.state as any)?.editUser;
    if (!hierarchyEditHandled.current && hierarchyUser) {
      hierarchyEditHandled.current = true;
      editUser(hierarchyUser);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const handleEditSubmit = (event: any) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidatedEdit(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
        setValidatedEdit(true);
        setErrorEdit("");
        const passwordError = validatePasswordOptional();
        if (passwordError) {
          setErrorEdit(passwordError);
          return;
        }

        let token = sessionStorage.getItem("token");
        fetch(ventrisConfig.url + '/api/user/' + selectedUser.id, {
          method: 'put',
          headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
          },
          body: JSON.stringify({
            email: email,
            password: password,
            name: firstName + " " + lastName,
            first_name: firstName,
            last_name: lastName,
            role: role,
            organization_id: organizationId,
          })
        }).then(function (response) {
          return response.json();
        }).then(async function (data) {
          if (data.success) {
            setShowEditModal(false);
            setPage(1);
            loadUsers(1);
          } else {
            setErrorEdit(data.message || "An error occured while editing this user. Please try again!");
          }
        });
    }
  };

  const createUser = () => {
    const defaultOrganizationId = availableUserOrganizations.find((organization: any) => String(organization.id) === String(user.organization_id))?.id
      ?? availableUserOrganizations[0]?.id
      ?? "-1";

    setSelectedUser({});
    setSelectedUserId(-1);
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setRole("Clinician");
    setOrganizationId(defaultOrganizationId);
    setShowCreateModal(true);
  }

  const createCsvTemplate = () => {
    return "Email Address,Password,First Name,Last Name,Role\n";
  };

  const handleDownloadTemplate = () => {
    const csvContent = createCsvTemplate();
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const parseCsv = (content: string) => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = "";
    let inQuotes = false;

    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = content[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        currentRow.push(currentField);
        currentField = "";
      } else if ((char === "\n" || char === "\r") && !inQuotes) {
        if (char === "\r" && nextChar === "\n") {
          i++;
        }
        currentRow.push(currentField);
        if (currentRow.some((field) => field.trim() !== "")) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = "";
      } else {
        currentField += char;
      }
    }

    currentRow.push(currentField);
    if (currentRow.some((field) => field.trim() !== "")) {
      rows.push(currentRow);
    }

    return rows;
  };

  const normalizeHeader = (header: string) => header.trim().toLowerCase();

  const normalizeRole = (value: string) => {
    const normalized = value.trim().toLowerCase();
    if (normalized === "admin") {
      return "Admin";
    }
    if (normalized === "clinician") {
      return "Clinician";
    }
    return "Clinician";
  };

  const openUploadUsers = () => {
    const defaultOrganizationId = availableUserOrganizations.find((organization: any) => String(organization.id) === String(user.organization_id))?.id
      ?? availableUserOrganizations[0]?.id
      ?? "-1";

    setValidatedUpload(false);
    setErrorUpload("");
    setUploadSummary("");
    setUploadOrganizationId(defaultOrganizationId);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const openActiveTeachers = async () => {
    const token = sessionStorage.getItem("token");
    setShowActiveTeachersModal(true);
    setLoadingManagedTeachers(true);
    setActiveTeachersError("");

    try {
      const response = await fetch(ventrisConfig.url + '/api/users/active-management', {
        method: 'get',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || `Unable to load ${terms.managedUsers.toLowerCase()}.`);
      }

      setManagedTeachers(data.users || []);
    } catch (error: any) {
      setActiveTeachersError(error.message || `Unable to load ${terms.managedUsers.toLowerCase()}.`);
    } finally {
      setLoadingManagedTeachers(false);
    }
  };

  const updateManagedTeacher = (userId: number, active: boolean) => {
    setManagedTeachers((currentTeachers) => currentTeachers.map((teacher: any) => (
      teacher.id === userId ? { ...teacher, active } : teacher
    )));
  };

  const saveActiveTeachers = async () => {
    const token = sessionStorage.getItem("token");
    setSavingManagedTeachers(true);
    setActiveTeachersError("");

    try {
      const response = await fetch(ventrisConfig.url + '/api/users/active-management', {
        method: 'put',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          users: managedTeachers.map((teacher: any) => ({
            id: teacher.id,
            active: !!teacher.active,
          }))
        })
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || `Unable to update ${terms.managedUsers.toLowerCase()}.`);
      }

      setShowActiveTeachersModal(false);
      setPage(1);
      loadUsers(1);
    } catch (error: any) {
      setActiveTeachersError(error.message || `Unable to update ${terms.managedUsers.toLowerCase()}.`);
    } finally {
      setSavingManagedTeachers(false);
    }
  };

  const handleCreateSubmit = (event: any) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidatedCreate(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
        setValidatedCreate(true);
        setErrorCreate("");
        const passwordError = validatePasswordRequired();
        if (passwordError) {
          setErrorCreate(passwordError);
          return;
        }

        let token = sessionStorage.getItem("token");
        fetch(ventrisConfig.url + '/api/user', {
          method: 'post',
          headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
          },
          body: JSON.stringify({
            email: email,
            password: password,
            name: firstName + " " + lastName,
            first_name: firstName,
            last_name: lastName,
            role: role,
            organization_id: organizationId,
          })
        }).then(function (response) {
          return response.json();
        }).then(async function (data) {
          if (data.success) {
            setShowCreateModal(false);
            setPage(1);
            loadUsers(1);
          } else {
            setErrorCreate(data.message || "An error occured while creating this user. Please try again!");
          }
        });
    }
  };

  const handleUploadSubmit = async (event: any) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false || !uploadFile) {
      setValidatedUpload(true);
      if (!uploadFile) {
        setErrorUpload("Please select a CSV file.");
      }
      focusFirstInvalidField(form);
      return;
    }

    setValidatedUpload(true);
    setErrorUpload("");
    setUploadSummary("");

    const requiredHeaders = ["email address", "password", "first name", "last name", "role"];
    const token = sessionStorage.getItem("token");

    try {
      const csvContent = await uploadFile.text();
      const rows = parseCsv(csvContent);
      if (rows.length < 2) {
        setErrorUpload("CSV must include a header row and at least one data row.");
        return;
      }

      const headerRow = rows[0].map((value) => normalizeHeader(value));
      const missingHeaders = requiredHeaders.filter((header) => !headerRow.includes(header));
      if (missingHeaders.length > 0) {
        setErrorUpload("CSV is missing required columns: " + missingHeaders.join(", "));
        return;
      }

      const emailIndex = headerRow.indexOf("email address");
      const passwordIndex = headerRow.indexOf("password");
      const firstNameIndex = headerRow.indexOf("first name");
      const lastNameIndex = headerRow.indexOf("last name");
      const roleIndex = headerRow.indexOf("role");

      const payloadRows = rows.slice(1).map((row) => {
        const firstNameValue = (row[firstNameIndex] || "").trim();
        const lastNameValue = (row[lastNameIndex] || "").trim();
        return {
          email: (row[emailIndex] || "").trim(),
          password: row[passwordIndex] || "",
          name: firstNameValue + " " + lastNameValue,
          first_name: firstNameValue,
          last_name: lastNameValue,
          role: normalizeRole((row[roleIndex] || "").trim()),
          organization_id: uploadOrganizationId,
        };
      });

      if (payloadRows.length === 0) {
        setErrorUpload("CSV must include at least one data row.");
        return;
      }

      const response = await fetch(ventrisConfig.url + '/api/users/bulk', {
        method: 'post',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          users: payloadRows
        })
      });
      const data = await response.json();

      const successCount = data.created_count || 0;
      const failedCount = data.failed_count || 0;
      const failedRows = Array.isArray(data.failed) ? data.failed : [];
      const rowErrors = failedRows.slice(0, 10).map((failure: any) => {
        const rowNumber = Number(failure.index) + 2;
        return "Row " + rowNumber + ": " + (failure.message || "API rejected this user.");
      });

      setUploadSummary("Upload complete. Added " + successCount + " user(s), failed " + failedCount + " row(s).");
      if (rowErrors.length > 0) {
        setErrorUpload(rowErrors.join(" "));
      }

	      setPage(1);
	      loadUsers(1);
	    } catch (err) {
	      setErrorUpload("Failed to parse or upload CSV. Please verify the file and try again.");
	    }
  };

  const deleteUser = (userId: any) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  }

  const actuallyDeleteUser = () => {
    let token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/user/' + selectedUserId, {
      method: 'delete',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setShowDeleteModal(false);
      setPage(1);
      loadUsers(1);
    });
  }

  const checkboxFormatter = (cell: any, row: any) => {
    return (
      <div>
      </div>
    );
  }

  const actionFormatter = (cell: any, row: any) => {
    const disabled = !!row.schoolday_sourced || !!row.schoolday_sourced_id;

    return (
      <div>
        <Button variant="link" size="sm" onClick={() => editUser(row)} disabled={disabled}>Edit</Button>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <Button variant="link" size="sm" onClick={() => deleteUser(row.id)} disabled={disabled}>Delete</Button>
      </div>
    );
  }

  const columns = [
    {
      dataField: 'id',
      text: '',
      formatter: checkboxFormatter
    },
    {
      dataField: 'organization',
      text: 'Organization',
      sort: true
    },
    {
      dataField: 'email',
      text: 'Email Address',
      sort: true
    },
    {
      dataField: 'first_name',
      text: 'First Name',
      sort: true
    },
    {
      dataField: 'last_name',
      text: 'Last Name',
      sort: true
    },
    {
      dataField: 'role',
      text: 'Role',
      sort: true,
      formatter: (cell: any) => cell === "Clinician" ? terms.teacher : cell
    },
    {
      dataField: 'id',
      text: 'Actions',
      formatter: actionFormatter
    }
  ];

  const updateFilter = (organizations: any) => {
    setOrganizationFilter(organizations.join('|'));
    setPage(1);
  };

  const handleTableChange = (type: string, { page, sizePerPage, sortField, sortOrder }: any) => {
    if ((sortField && sortFieldName != sortField) || (sortDirection && sortDirection != sortOrder)) {
      setPage(1);
    } else {
      setPage(page);
    }
    setSizePerPage(sizePerPage);
    setSortFieldName(sortField ? sortField : "");
    setSortDirection(sortOrder ? sortOrder : "");
  }

  const availableUserOrganizations = organizations;
  const canManageUsersManually = availableUserOrganizations.length > 0;

  return (
    <Container fluid className="ventris-content-wrapper ventris-users">
      <Header portal={sessionStorage.getItem("portal")!} page="users" />
      <Container as="main" id="main-content" tabIndex={-1}>
        <Row>
          <Col>
            <h1 className="page-header align-items-center">
              <GroupPageIcon />
              <div className="flex-grow">{terms.managedUsers}</div>
              <Form.Control type="search" className="ventris-search" aria-label={`Search ${terms.managedUsers.toLowerCase()}`} placeholder={`Search ${terms.managedUsers.toLowerCase()}`} value={search} onChange={(event) => setSearch(event.target.value)} />
              <div className="d-flex gap-2">
                <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal" onClick={() => createUser()} disabled={!canManageUsersManually}><GroupIcon /> Add a {terms.managedUser}</Button>
                <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-teal" onClick={() => openUploadUsers()} disabled={!canManageUsersManually}>Upload {terms.managedUsers}</Button>
                <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-teal" onClick={openActiveTeachers}>Manage Active {terms.managedUsers}</Button>
              </div>
            </h1>
            {organizationsLoaded && !canManageUsersManually ? <div className="mb-3 text-muted">No accessible organizations are available for user management.</div> : null}
            <div className={showFilters ? '' : 'hidden'}>
              <OrganizationFilter updateFilter={updateFilter} organizations={organizations} />
            </div>
            <div className="flex flex-row">
              <div className="flex-grow">
                <Tabs defaultActiveKey="view-all">
                  <Tab eventKey="view-all" title="View All">
                  </Tab>
                </Tabs>
              </div>
              <div className="ventris-action-links">
                <Button variant="link" size="sm" className="ventris-action-link" onClick={() => setShowFilters(!showFilters)}><FilterIcon /> {showFilters ? 'Hide Filter' : 'Show Filter'}</Button>
              </div>
            </div>
              <ResponsiveTableRegion label={terms.managedUsers}>
                <BootstrapTable keyField='id' data={users} columns={columns} bordered={false} remote={{pagination: true, sort: true}} pagination={paginationFactory({ page, sizePerPage, totalSize })} onTableChange={handleTableChange} />
              </ResponsiveTableRegion>
          </Col>
        </Row>
      </Container>
      <Modal
        show={showActiveTeachersModal}
        onHide={() => !savingManagedTeachers && setShowActiveTeachersModal(false)}
        centered
        dialogClassName="manage-active-teachers-modal"
      >
        <Modal.Body className="manage-active-teachers-body" aria-busy={loadingManagedTeachers || savingManagedTeachers}>
          <AsyncStatus
            busy={loadingManagedTeachers || savingManagedTeachers}
            busyMessage={savingManagedTeachers ? `Saving active ${terms.managedUsers.toLowerCase()}.` : `Loading ${terms.managedUsers.toLowerCase()}.`}
            completeMessage={`${terms.managedUsers} updated.`}
          />
          <h2>Manage Active {terms.managedUsers}</h2>
          <p>Select {terms.managedUsers.toLowerCase()} who should be active and able to administer tests. Deselect users who should not have access.</p>

          {activeTeachersError !== "" ? <Alert variant="danger">{activeTeachersError}</Alert> : null}

          {loadingManagedTeachers ? (
            <div className="manage-active-teachers-loading">Loading {terms.managedUsers.toLowerCase()}...</div>
          ) : (
            <ResponsiveTableRegion label={`Active ${terms.managedUsers}`} className="manage-active-teachers-table-wrapper">
              <table className="manage-active-teachers-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email Address</th>
                    <th>Organization</th>
                    <th>Active?</th>
                  </tr>
                </thead>
                <tbody>
                  {managedTeachers.map((teacher: any) => (
                    <tr key={teacher.id} className={teacher.active ? "" : "inactive"}>
                      <td>{teacher.name || `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim()}</td>
                      <td>{teacher.email}</td>
                      <td>{teacher.organization}</td>
                      <td>
                        <Form.Check
                          aria-label={`${teacher.name || teacher.email} active`}
                          checked={!!teacher.active}
                          onChange={(event) => updateManagedTeacher(teacher.id, event.target.checked)}
                        />
                      </td>
                    </tr>
                  ))}
                  {managedTeachers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center">No {terms.managedUsers.toLowerCase()} found.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </ResponsiveTableRegion>
          )}

          <div className="manage-active-teachers-actions">
            <Button
              variant="secondary"
              className="ventris-button-secondary ventris-button-secondary-teal"
              onClick={() => setShowActiveTeachersModal(false)}
              disabled={savingManagedTeachers}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="ventris-button-primary ventris-button-primary-teal"
              onClick={saveActiveTeachers}
              disabled={loadingManagedTeachers || savingManagedTeachers}
            >
              {savingManagedTeachers ? "Saving..." : "Save & Update"}
            </Button>
          </div>

          <div className="manage-active-teachers-help">
            <strong>Need help?</strong> Contact us by email at <a href="mailto:help@ventrislearning.com">help@ventrislearning.com</a> or by phone at <a href="tel:800-555-1234">800-555-1234</a>.
          </div>
        </Modal.Body>
      </Modal>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this item? Deletion is permanent and cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-teal half-width" onClick={() => setShowDeleteModal(false)}>No, Cancel</Button>
          <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal half-width" onClick={() => actuallyDeleteUser()}>Yes, Confirm Delete</Button>
        </Modal.Footer>
      </Modal>
      <Offcanvas show={showEditModal} onHide={() => setShowEditModal(false)} placement="end" scroll={false} backdrop={true} aria-labelledby="edit-user-title">
          <Offcanvas.Header className="offcanvas-header-yellow" closeButton>
              <Offcanvas.Title id="edit-user-title">Edit {terms.managedUser}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form className="edit-user-form" validated={validatedEdit} ref={editFormRef} onSubmit={handleEditSubmit}>
              {errorEdit !== "" ? <Alert variant="danger" className="mb-3">{errorEdit}</Alert> : null}
              <Form.Group className="mb-3" controlId="edit-user-email">
                  <Form.Control required type="email" autoComplete="email" placeholder="Email" value={email} onChange={(event) => setEmail(event.target.value)} />
                  <Form.Control.Feedback type="invalid">Please enter an email.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-user-password">
                  <Form.Control type="password" autoComplete="new-password" placeholder="Password (Optional)" value={password} onChange={(event) => setPassword(event.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-user-first-name">
                  <Form.Control required type="text" autoComplete="given-name" placeholder="First Name" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                  <Form.Control.Feedback type="invalid">Please enter a first name.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-user-last-name">
                  <Form.Control required type="text" autoComplete="family-name" placeholder="Last Name" value={lastName} onChange={(event) => setLastName(event.target.value)} />
                  <Form.Control.Feedback type="invalid">Please enter a last name.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-user-role">
                  <Form.Select required aria-label="Role" value={role} onChange={(event) => setRole(event.target.value)}>
                    <option value="Clinician">{terms.teacher}</option>
                    <option value="Admin">Admin</option>
                  </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-user-organization">
                  <OrganizationPicker
                    organizations={availableUserOrganizations}
                    value={organizationId}
                    onChange={setOrganizationId}
                  />
              </Form.Group>
              <div className="modal-form-actions">
                <Button type="submit" variant="primary" className="ventris-button-primary ventris-button-primary-teal full-width">Save</Button>
              </div>
            </Form>
          </Offcanvas.Body>
      </Offcanvas>
      <Offcanvas show={showCreateModal} onHide={() => setShowCreateModal(false)} placement="end" scroll={false} backdrop={true} aria-labelledby="create-user-title">
          <Offcanvas.Header className="offcanvas-header-yellow" closeButton>
              <Offcanvas.Title id="create-user-title">Create {terms.managedUser}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form className="edit-user-form" validated={validatedCreate} ref={createFormRef} onSubmit={handleCreateSubmit}>
              {errorCreate !== "" ? <Alert variant="danger" className="mb-3">{errorCreate}</Alert> : null}
              <Form.Group className="mb-3" controlId="create-user-email">
                  <Form.Control required type="email" autoComplete="email" placeholder="Email" value={email} onChange={(event) => setEmail(event.target.value)} />
                  <Form.Control.Feedback type="invalid">Please enter an email.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="create-user-password">
                  <Form.Control required type="password" autoComplete="new-password" placeholder="Password" value={password} onChange={(event) => setPassword(event.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3" controlId="create-user-first-name">
                  <Form.Control required type="text" autoComplete="given-name" placeholder="First Name" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                  <Form.Control.Feedback type="invalid">Please enter a first name.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="create-user-last-name">
                  <Form.Control required type="text" autoComplete="family-name" placeholder="Last Name" value={lastName} onChange={(event) => setLastName(event.target.value)} />
                  <Form.Control.Feedback type="invalid">Please enter a last name.</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="create-user-role">
                  <Form.Select required aria-label="Role" value={role} onChange={(event) => setRole(event.target.value)}>
                    <option value="Clinician">{terms.teacher}</option>
                    <option value="Admin">Admin</option>
                  </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="create-user-organization">
                  <OrganizationPicker
                    organizations={availableUserOrganizations}
                    value={organizationId}
                    onChange={setOrganizationId}
                  />
              </Form.Group>
              <div className="modal-form-actions">
                <Button type="submit" variant="primary" className="ventris-button-primary ventris-button-primary-teal full-width">Save</Button>
              </div>
            </Form>
          </Offcanvas.Body>
      </Offcanvas>
      <Offcanvas show={showUploadModal} onHide={() => setShowUploadModal(false)} placement="end" scroll={false} backdrop={true} aria-labelledby="upload-users-title">
          <Offcanvas.Header className="offcanvas-header-yellow" closeButton>
              <Offcanvas.Title id="upload-users-title">Upload {terms.managedUsers}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Form className="edit-user-form" validated={validatedUpload} ref={uploadFormRef} onSubmit={handleUploadSubmit}>
              {errorUpload !== "" ? <Alert variant="danger" className="mb-3">{errorUpload}</Alert> : null}
              {uploadSummary !== "" ? <Alert variant="success" className="mb-3">{uploadSummary}</Alert> : null}
              <Form.Group className="mb-3" controlId="uploadOrganization">
                  <Form.Label>Organization:</Form.Label>
                  <OrganizationPicker
                    organizations={availableUserOrganizations}
                    value={uploadOrganizationId}
                    onChange={setUploadOrganizationId}
                  />
              </Form.Group>
              <Form.Group className="mb-3" controlId="uploadCsvFile">
                  <Form.Label>CSV File:</Form.Label>
                  <Form.Control
                    required
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(event: any) => setUploadFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)}
                  />
                  <Form.Control.Feedback type="invalid">Please choose a CSV file.</Form.Control.Feedback>
              </Form.Group>
              <div className="modal-form-actions">
                <Button type="submit" variant="primary" className="ventris-button-primary ventris-button-primary-teal full-width">Upload</Button>
              </div>
              <div className="text-center mt-3">
                <Button variant="link" className="ventris-action-link" onClick={handleDownloadTemplate}>Download Template CSV</Button>
              </div>
            </Form>
          </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}

export default AdminUsers;
